import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, clientsAPI, productsAPI } from '../services/api';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack,
  TablePagination,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

const STATUS_KEYS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = { pending: 'warning', confirmed: 'info', processing: 'primary', shipped: 'secondary', delivered: 'success', cancelled: 'error' };

const ITEMS_PAGE_SIZE = 5;

const Orders = () => {
  const { user, hasRole } = useAuth();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', clientId: '', fromDate: '', toDate: '' });
  const [viewOrder, setViewOrder] = useState(null);
  const [viewItemsPage, setViewItemsPage] = useState(0);
  const [editOrder, setEditOrder] = useState(null);
  const [editItemsPage, setEditItemsPage] = useState(0);
  const [editForm, setEditForm] = useState({ deliveryDate: null, notes: '', status: '' });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.clientId) params.clientId = filters.clientId;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      const response = await ordersAPI.getAll(params);
      setOrders(response.data);
      setError('');
    } catch (err) {
      setError(t('common.error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    if (hasRole(['admin', 'operator'])) {
      try {
        const response = await clientsAPI.getAll();
        setClients(response.data);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const resolveProductName = (productId) =>
    products.find((p) => p.id === productId)?.name ?? productId;

  useEffect(() => { fetchOrders(); fetchClients(); fetchProducts(); }, []);
  useEffect(() => { fetchOrders(); }, [filters]);

  const handleEditClick = (order) => {
    setEditOrder(order);
    setEditItemsPage(0);
    setEditForm({
      // Parse DATEONLY as local noon to avoid UTC-midnight-shifting to previous day in GMT-3
      deliveryDate: new Date(order.deliveryDate + 'T12:00:00'),
      notes: order.notes || '',
      status: order.status,
    });
  };

  const handleEditSave = async () => {
    try {
      await ordersAPI.update(editOrder.id, {
        deliveryDate: format(editForm.deliveryDate, 'yyyy-MM-dd'),
        notes: editForm.notes,
        status: editForm.status,
      });
      setEditOrder(null);
      fetchOrders();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    }
  };

  const handleCancelClick = (order) => { setOrderToCancel(order); setCancelDialogOpen(true); };

  const handleConfirmCancel = async () => {
    try {
      await ordersAPI.cancel(orderToCancel.id);
      setCancelDialogOpen(false);
      setOrderToCancel(null);
      fetchOrders();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      fetchOrders();
    } catch (err) {
      setError(t('common.error'));
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.clientId) params.clientId = filters.clientId;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      const response = await ordersAPI.exportCsv(params);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pedidos-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  const canEditOrCancel = (order) => {
    if (order.status === 'cancelled') return false;
    if (hasRole(['admin', 'operator'])) return true;
    // Compare DATEONLY string against Argentina today as UTC-midnight dates
    const arToday = new Date(new Date().toLocaleDateString('sv', { timeZone: 'America/Argentina/Buenos_Aires' }));
    const daysUntilDelivery = Math.round((new Date(order.deliveryDate) - arToday) / 86400000);
    return daysUntilDelivery >= 2;
  };

  // Format a DATEONLY string ('YYYY-MM-DD') as 'dd/MM/yyyy'
  const formatDate = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
  };

  // Format a UTC timestamp for display in Argentina timezone
  const formatDateTime = (s) => {
    if (!s) return '';
    return new Intl.DateTimeFormat('es-AR', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(s));
  };

  const orderGross = (o) => parseFloat(o.totalAmount) || 0;
  const orderDiscount = (o) => parseFloat(o.discount) || 0;
  const orderNet = (o) => orderGross(o) * (1 - orderDiscount(o) / 100);

  if (loading && orders.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {hasRole(['admin', 'operator']) ? t('orders.allOrders') : t('orders.title')}
        </Typography>
        {hasRole(['admin']) && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting}
            size="small"
          >
            {exporting ? t('orders.exporting') : t('orders.exportCsv')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      {hasRole(['admin', 'operator']) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('orders.filters')}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.status')}</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label={t('orders.status')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {STATUS_KEYS.map((s) => (
                  <MenuItem key={s} value={s}>{t(`orders.statusOptions.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.client')}</InputLabel>
              <Select
                value={filters.clientId}
                onChange={(e) => setFilters({ ...filters, clientId: e.target.value })}
                label={t('orders.client')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth label={t('orders.fromDate')} type="date" size="small"
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth label={t('orders.toDate')} type="date" size="small"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.orderNumber')}</TableCell>
              {hasRole(['admin', 'operator']) && <TableCell sx={{ fontWeight: 600 }}>{t('orders.client')}</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.deliveryDate')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.total')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.created')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('orders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.orderNumber}</TableCell>
                {hasRole(['admin', 'operator']) && (
                  <TableCell>
                    {order.client?.companyName}
                    {order.client?.clientNumber && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {order.client.clientNumber}
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell>{formatDate(order.deliveryDate)}</TableCell>
                <TableCell>
                  {orderDiscount(order) > 0 ? (
                    <>
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        ${orderGross(order).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${orderNet(order).toFixed(2)}
                        <Typography component="span" variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                          (-{orderDiscount(order)}%)
                        </Typography>
                      </Typography>
                    </>
                  ) : (
                    <>${orderGross(order).toFixed(2)}</>
                  )}
                </TableCell>
                <TableCell>
                  {hasRole(['admin', 'operator']) ? (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select value={order.status} onChange={(e) => handleStatusChange(order.id, e.target.value)} size="small">
                        {STATUS_KEYS.map((s) => (
                          <MenuItem key={s} value={s}>{t(`orders.statusOptions.${s}`)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      label={t(`orders.statusOptions.${order.status}`)}
                      color={STATUS_COLORS[order.status] || 'default'}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                <TableCell>
                  <Tooltip title={t('orders.view')}>
                    <IconButton onClick={() => { setViewOrder(order); setViewItemsPage(0); }} size="small"><ViewIcon /></IconButton>
                  </Tooltip>
                  {canEditOrCancel(order) && (
                    <>
                      <Tooltip title={t('orders.edit')}>
                        <IconButton size="small" onClick={() => handleEditClick(order)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('orders.cancel')}>
                        <IconButton onClick={() => handleCancelClick(order)} size="small" color="error">
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Order Dialog */}
      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="md" fullWidth>
        {viewOrder && (
          <>
            <DialogTitle>{t('orders.orderNumber')} {viewOrder.orderNumber}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('orders.deliveryDate')}</Typography>
                  <Typography variant="body1">{formatDate(viewOrder.deliveryDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">{t('orders.status')}</Typography>
                  <Chip
                    label={t(`orders.statusOptions.${viewOrder.status}`)}
                    color={STATUS_COLORS[viewOrder.status] || 'default'}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{t('orders.orderItems')}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t('orders.product')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.qty')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.price')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.subtotal')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewOrder.items.slice(viewItemsPage * ITEMS_PAGE_SIZE, (viewItemsPage + 1) * ITEMS_PAGE_SIZE).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{resolveProductName(item.productId)}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">${parseFloat(item.price).toFixed(2)}</TableCell>
                        <TableCell align="right">${(item.quantity * parseFloat(item.price)).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>{t('orders.totalAmount')}:</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>${orderGross(viewOrder).toFixed(2)}</TableCell>
                    </TableRow>
                    {orderDiscount(viewOrder) > 0 && (
                      <>
                        <TableRow>
                          <TableCell colSpan={3} align="right" sx={{ color: 'success.main' }}>
                            {t('orders.discount')} ({orderDiscount(viewOrder)}%):
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            −${(orderGross(viewOrder) - orderNet(viewOrder)).toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>{t('orders.netAmount')}:</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>${orderNet(viewOrder).toFixed(2)}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
                {viewOrder.items.length > ITEMS_PAGE_SIZE && (
                  <TablePagination
                    component="div"
                    count={viewOrder.items.length}
                    page={viewItemsPage}
                    onPageChange={(_, p) => setViewItemsPage(p)}
                    rowsPerPage={ITEMS_PAGE_SIZE}
                    rowsPerPageOptions={[ITEMS_PAGE_SIZE]}
                  />
                )}
              </TableContainer>
              {viewOrder.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('orders.notes')}</Typography>
                  <Typography variant="body2">{viewOrder.notes}</Typography>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewOrder(null)}>{t('common.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={!!editOrder} onClose={() => setEditOrder(null)} maxWidth="sm" fullWidth>
        {editOrder && (
          <>
            <DialogTitle>{t('orders.edit')} — {editOrder.orderNumber}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <DatePicker
                  label={t('orders.deliveryDate')}
                  value={editForm.deliveryDate}
                  onChange={(date) => setEditForm({ ...editForm, deliveryDate: date })}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>{t('orders.status')}</InputLabel>
                  <Select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    label={t('orders.status')}
                  >
                    {STATUS_KEYS.map((s) => (
                      <MenuItem key={s} value={s}>{t(`orders.statusOptions.${s}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth multiline rows={2}
                  label={t('orders.notes')}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder={t('orders.notesPlaceholder')}
                  size="small"
                />
                <Divider />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('orders.orderItems')}</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t('orders.product')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.qty')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.price')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{t('orders.subtotal')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editOrder.items.slice(editItemsPage * ITEMS_PAGE_SIZE, (editItemsPage + 1) * ITEMS_PAGE_SIZE).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{resolveProductName(item.productId)}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">${parseFloat(item.price).toFixed(2)}</TableCell>
                          <TableCell align="right">${(item.quantity * parseFloat(item.price)).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>{t('orders.totalAmount')}:</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>${parseFloat(editOrder.totalAmount).toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  {editOrder.items.length > ITEMS_PAGE_SIZE && (
                    <TablePagination
                      component="div"
                      count={editOrder.items.length}
                      page={editItemsPage}
                      onPageChange={(_, p) => setEditItemsPage(p)}
                      rowsPerPage={ITEMS_PAGE_SIZE}
                      rowsPerPageOptions={[ITEMS_PAGE_SIZE]}
                    />
                  )}
                </TableContainer>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditOrder(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleEditSave} variant="contained">{t('common.update')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>{t('orders.cancelConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('orders.cancelConfirmText', { number: orderToCancel?.orderNumber })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('orders.keepOrder')}</Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            {t('orders.confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
